import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from 'helpers/common';
import { RootState } from 'redux/store';
import { IPaginationResponse } from 'interfaces/responces/IPagination.response';
import { IFilter } from 'interfaces/IFilter';

export interface IReceiptWriteResponse {
  id: number;
  companyId: number;
  stockOwnerId: number;
  code: string;
  status: string;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface IReceiptWriteRequest {
  stockOwnerId: number;
  note?: string;
  receiptItems: Array<{ productId: number; qty: number }>;
  attributeValues?: Array<{ definitionId: number; value: unknown }>;
}

export interface IReceiptUpdateRequest {
  note?: string;
  status?: string;
  receiptItems?: Array<{ productId: number; qty: number }>;
  attributeValues?: Array<{ definitionId: number; value: unknown }>;
}

export interface IReceiptListItem {
  id: number;
  code: string;
  status: string;
  note: string | null;
  stockOwnerId: number;
  stockOwner?: { id: number; code: string; name?: string };
  createdAt: string;
  updatedAt: string;
  /** Draft (status 0) only: whether all active required attributes are present. */
  attributesValid?: boolean;
  attributeValues?: Array<{
    definitionId: number;
    value: unknown;
    definition: {
      id: number;
      code: string;
      name: string;
      type: string;
      unit: string | null;
      isRequired: boolean;
      isActive: boolean;
      validation: unknown;
      options: unknown;
      sortOrder: number;
    };
  }>;
}

export interface IInvalidAttributeDraftReceipts {
  count: number;
  list: Array<{
    id: number;
    code: string;
    status: string;
    stockOwnerId: number;
  }>;
}

export interface IReceiptDetailItem extends IReceiptListItem {
  receiptItems: Array<{
    id: number;
    productId: number;
    qty: number;
    product?: {
      id: number;
      sku?: string;
      code?: string;
      name?: string | Record<string, string>;
      title?: string | Record<string, string>;
      secondaryCode?: string | Record<string, string>;
      shortDescription?: string | Record<string, string>;
      longDescription?: string | Record<string, string>;
      description?: string | Record<string, string>;
      descriptionShort?: string | Record<string, string>;
    };
  }>;
}

export const receiptsApi = createApi({
  reducerPath: 'receiptsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ['Receipt'],
  endpoints: (build) => ({
    createReceipt: build.mutation<IReceiptWriteResponse, IReceiptWriteRequest>({
      query: (body) => ({ url: '/receipts/create', method: 'POST', body }),
      transformResponse: (response: { data: IReceiptWriteResponse }) => response.data,
      invalidatesTags: ['Receipt'],
    }),
    getReceiptsList: build.query<IPaginationResponse<IReceiptListItem>, IFilter>({
      query: (params) => ({ url: '/receipts/list', method: 'GET', params }),
      transformResponse: (response: { data: IPaginationResponse<IReceiptListItem> }) =>
        response.data,
      providesTags: ['Receipt'],
    }),
    getInvalidAttributeDraftReceipts: build.query<IInvalidAttributeDraftReceipts, void>({
      query: () => ({ url: '/receipts/invalid-attribute-drafts', method: 'GET' }),
      transformResponse: (response: { data: IInvalidAttributeDraftReceipts }) =>
        response.data,
      providesTags: ['Receipt'],
    }),
    getReceiptById: build.query<IReceiptDetailItem, number>({
      query: (id) => ({ url: `/receipts/${id}`, method: 'GET' }),
      transformResponse: (response: { data: IReceiptDetailItem }) => response.data,
      providesTags: (_result, _err, id) => [{ type: 'Receipt', id }],
    }),
    updateReceipt: build.mutation<IReceiptWriteResponse, { id: number; body: IReceiptUpdateRequest }>({
      query: ({ id, body }) => ({ url: `/receipts/${id}`, method: 'PATCH', body }),
      transformResponse: (response: { data: IReceiptWriteResponse }) => response.data,
      invalidatesTags: ['Receipt'],
    }),
    getReceipts: build.query<unknown[], void>({
      query: () => ({ url: '/receipts', method: 'GET' }),
      transformResponse: (response: { data: unknown[] }) => response.data,
      providesTags: ['Receipt'],
    }),
  }),
});

export const {
  useCreateReceiptMutation,
  useGetReceiptsListQuery,
  useGetInvalidAttributeDraftReceiptsQuery,
  useGetReceiptByIdQuery,
  useUpdateReceiptMutation,
  useGetReceiptsQuery,
} = receiptsApi;
